import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { restaurantApi, foodApi, inr, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { RingModal } from "./RingModal";

const REJECT_REASONS = ["Out of stock", "Too busy", "Closing soon", "Item unavailable", "Other"];
const PREP_PRESETS = [10, 15, 20, 30, 45];

// GLOBAL new-order ring for restaurants. Mounted once in AppLayout so a new order rings and
// pops the accept/reject sheet on EVERY page — not just the Orders board. On the Orders page
// itself the in-page ring owns it, so this stays silent there to avoid a double ring.
export default function RestaurantOrderRing() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onOrdersPage = pathname.startsWith("/app/restaurant/orders");
  const canOrders = !user?.is_manager || user?.permissions == null || (user?.permissions || []).includes("orders");
  const active = user?.role === "restaurant" && canOrders && !onOrdersPage;

  const [orders, setOrders] = useState([]);
  const [ringId, setRingId] = useState(null);
  const [prep, setPrep] = useState(20);
  const defaultPrep = useRef(20);
  const snoozedUntil = useRef({});

  useEffect(() => {
    if (!active) { setRingId(null); return undefined; }
    let on = true;
    restaurantApi.mine().then((r) => { if (on && r?.pickup_prep_min) defaultPrep.current = r.pickup_prep_min; }).catch(() => {});
    const poll = async () => {
      try {
        const list = await restaurantApi.orders();
        if (!on) return;
        setOrders(list || []);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { on = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [active]);

  // The oldest still-pending order that isn't currently snoozed drives the ring.
  const ringOrder = useMemo(() => {
    if (!active) return null;
    const now = Date.now();
    const pending = (orders || [])
      .filter((o) => o.status === "placed" && (!snoozedUntil.current[o.order_id] || snoozedUntil.current[o.order_id] <= now))
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return pending[0] || null;
  }, [orders, active, ringId]);

  useEffect(() => { if (ringOrder) { setRingId(ringOrder.order_id); setPrep(ringOrder.prep_min || defaultPrep.current || 20); } else setRingId(null); }, [ringOrder?.order_id]);

  const respond = async (status, reason) => {
    const o = ringOrder; if (!o) return;
    try {
      const extra = status === "accepted" ? { prep_min: prep } : undefined;
      await foodApi.orderStatus(o.order_id, status, reason, undefined, extra);
      setOrders((prev) => prev.map((x) => (x.order_id === o.order_id ? { ...x, status } : x)));
      toast.success(status === "accepted" ? `Order accepted — ready in ~${prep} min` : "Order rejected");
      if (status === "accepted") navigate("/app/restaurant/orders");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const snooze = () => { if (ringOrder) snoozedUntil.current[ringOrder.order_id] = Date.now() + 60000; setRingId(null); setOrders((p) => [...p]); toast.info("Snoozed — we'll ring again in 1 minute"); };

  if (!active || !ringOrder) return null;

  return (
    <RingModal
      open={!!ringOrder}
      title="New order!"
      subtitle={`${ringOrder.customer_name} · ${inr(ringOrder.total)}`}
      acceptLabel="Accept order"
      rejectLabel="Reject"
      requireRejectReason
      rejectPresets={REJECT_REASONS}
      onAccept={() => respond("accepted")}
      onReject={(reason) => respond("rejected", reason)}
      onSnooze={snooze}
    >
      <div className="space-y-2" data-testid="global-order-ring">
        <p className="text-sm font-semibold text-ink">#{String(ringOrder.order_id).slice(-6).toUpperCase()} · {ringOrder.customer_name}</p>
        <ul className="flex flex-wrap gap-2 text-sm">{(ringOrder.items || []).map((i, idx) => <li key={idx} className="rounded-full bg-surface-100 px-3 py-1 text-ink">{i.qty} × {i.name}</li>)}</ul>
        <p className="text-xs text-ink-muted">{ringOrder.delivery_address}</p>
        <p className="text-xs font-bold uppercase tracking-wide text-amber-700">{ringOrder.order_type === "pickup" ? "Self pick-up order" : "Delivery order"}</p>
        <p className="text-xs font-medium text-ink-soft">{ringOrder.payment_method === "razorpay" ? "Paid online" : ringOrder.order_type === "pickup" ? "Pay at restaurant" : "Cash on delivery"}</p>
        <div>
          <p className="mb-1 text-xs font-medium text-ink-soft">Food ready in</p>
          <div className="flex flex-wrap gap-2">
            {PREP_PRESETS.map((m) => (
              <button key={m} type="button" onClick={() => setPrep(m)} className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${prep === m ? "bg-brand text-white" : "border border-surface-300 text-ink-soft hover:border-brand-300"}`} data-testid={`ring-prep-${m}`}>{m}m</button>
            ))}
          </div>
        </div>
      </div>
    </RingModal>
  );
}
